/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import moment from "moment";
import SystemUser from "./messages/SystemUser";
import Recipient from "./messages/Recipient";
import { Loader } from "@/subframe/components/Loader";

interface Message {
  message_id?: string;
  id?: string;
  recipient?: string;
  system_user?: string;
  created_at: string;
  status?: "sent" | "delivered" | "read" | "passed";
  recipient_payload?: {
    entry: [
      {
        changes: [
          {
            value: {
              messages: [
                {
                  type:
                    | "text"
                    | "video"
                    | "document"
                    | "image"
                    | "audio"
                    | "button";
                  text?: {
                    body: string;
                  };
                  video?: {
                    caption?: string;
                    id?: number;
                  };
                  document?: {
                    caption?: string;
                    id?: number;
                    filename?: string;
                    mime_type?: string;
                  };
                  image?: {
                    caption?: string;
                    id?: number;
                  };
                  audio?: {
                    voice: boolean;
                  };
                }
              ];
            };
          }
        ];
      }
    ];
  };
  system_user_payload?: {
    image?: {
      id: number;
      caption: string;
    };
    document?: {
      id: number;
      caption: string;
      mime_type: any;
    };
    video?: {
      id: number;
      caption: string;
    };
    type: any;
    template: any;
  };
}

interface MessagesDataWithPagination {
  items: Message[];
  total?: number;
  page?: number;
  size?: number;
}

interface MessageProps {
  messages: MessagesDataWithPagination;
  scrollRef: React.RefObject<HTMLDivElement>;
  is24HoursWindowShow: boolean;
}

const Message: React.FC<MessageProps> = ({
  messages,
  scrollRef,
  is24HoursWindowShow,
}) => {
  // Group messages by date
  const groupedMessages = messages?.items?.reduce((acc, message) => {
    const messageDate = moment(message.created_at).format("YYYY-MM-DD");
    if (!acc[messageDate]) {
      acc[messageDate] = [];
    }
    acc[messageDate].push(message);
    return acc;
  }, {} as Record<string, typeof messages.items>);

  return (
    <div className="relative flex w-full grow shrink-0 basis-0 flex-col items-start bg-gray-50 px-4 py-4 overflow-auto custom-scrollbar">
      {/* 24 Hours Window Alert */}
      {is24HoursWindowShow && (
        <div className="fixed top-20 left-1/2 w-full max-w-md z-20 flex justify-center items-center -translate-x-1/2 md:left-2/3 md:-translate-x-1/2">
          <div className="bg-neutral-100 rounded-lg shadow-lg border border-orange-500/40 p-3 mx-4">
            <div className="flex items-start gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-500/25 rounded-full flex items-center justify-center border border-orange-500/35">
                <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-semibold text-default-font mb-0.5">24-hour window closed</h3>
                <p className="text-xs text-subtext-color leading-relaxed">
                  You can only send personalized messages within 24 hours of the last customer message.
                </p>
              </div>
              <button className="flex-shrink-0 w-5 h-5 bg-neutral-200 hover:bg-neutral-300 rounded-full flex items-center justify-center transition-colors border border-neutral-border">
                <svg className="w-3 h-3 text-subtext-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages Container */}
      <div className="flex w-full flex-col items-start gap-4">
        {groupedMessages ? (
          Object.entries(groupedMessages).map(([date, dayMessages]) => (
            <div key={date} className="w-full space-y-2">
              {/* Date Separator */}
              <div className="flex items-center justify-center my-5">
                <div className="flex items-center gap-3 w-full max-w-md">
                  <div className="flex-1 h-px bg-neutral-300"></div>
                  <div className="px-3 py-1.5 bg-neutral-100 rounded-full border border-solid border-neutral-border">
                    <span className="text-xs font-medium text-default-font">
                      {moment(date).isSame(moment(), "day")
                        ? "Today"
                        : moment(date).isSame(moment().subtract(1, 'day'), "day")
                        ? "Yesterday"
                        : moment(date).format("MMM D, YYYY")}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-neutral-300"></div>
                </div>
              </div>

              {/* Messages for this date */}
              <div className="space-y-1">
                {dayMessages.map((message) => (
                  <React.Fragment key={message.id}>
                    {message.system_user ? (
                      <SystemUser message={message} scrollRef={scrollRef} />
                    ) : (
                      <Recipient message={message} scrollRef={scrollRef} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center w-full py-16">
            <div className="flex flex-col items-center gap-3">
              <Loader size="medium" />
              <p className="text-subtext-color text-xs">Loading messages...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;
